/**
 * SpectraIQ role matrix – mirrors the backend SpectraIqRole enum.
 *
 * SUPER_ADMIN is deliberately NOT assignable from the dealership UI: it's a
 * cross-tenant PMDG operations role. It appears in ROLE_META (so an existing
 * super-admin still renders) but is excluded from ASSIGNABLE_ROLES.
 */
export const ROLE_META = {
  SUPER_ADMIN:       { label: 'Super Admin',       short: 'Super',      desc: 'PMDG operations Â· cross-tenant', tone: 'danger' },
  DEALER_PRINCIPAL:  { label: 'Dealer Principal',  short: 'Principal',  desc: 'Full control of this dealership', tone: 'accent' },
  // Same access as Dealer Principal. Separate roles so the audit log records who
  // actually acted – "Dealer Principal approved this" when it was the CEO is the
  // kind of entry that causes trouble later.
  //
  // The label reads "GM – Sales" because the client's revised org chart of
  // 2026-09-30 splits GM into Sales and Service branches. The enum id stays
  // GM_SALES for compatibility with issued Keycloak roles; only the
  // display label reflects the split. GM – Service would be a separate role
  // added later (currently mapped through SERVICE_MANAGER, which is
  // non-assignable in the sales-only build).
  GM_SALES:   { label: 'GM – Sales',        short: 'GM Sales',   desc: 'Runs vehicle sales end-to-end; same permissions as Dealer Principal', tone: 'accent' },
  // GM – Service – peer of GM – Sales at the leadership level per the client's
  // revised chart of 2026-09-30. Runs workshop and parts as one profit centre.
  // Access in this sales-only build mirrors Service Manager (the one place
  // sales and service meet); expand when the service module lands.
  GM_SERVICE:        { label: 'GM – Service',      short: 'GM Svc',     desc: 'Runs workshop and parts; leadership peer of GM – Sales', tone: 'accent' },
  CEO:               { label: 'CEO',               short: 'CEO',        desc: 'Same access as Dealer Principal', tone: 'accent' },
  SALES_MANAGER:     { label: 'Sales Manager',     short: 'Sales Mgr',  desc: 'Runs the new and fleet desks day-to-day', tone: 'accent' },
  // Used Car Manager – added at the client's request 2026-09-30 as a sibling
  // of Sales Manager. Owns the used business end-to-end; row-scoped to USED
  // stock via CallerScope.restrictedVehicleConditions on the backend.
  USED_CAR_MANAGER:  { label: 'Used Car Manager',  short: 'Used Mgr',   desc: 'Owns the used business – buying, pricing, reconditioning, selling', tone: 'accent' },
  SERVICE_MANAGER:   { label: 'Service Manager',   short: 'Svc Mgr',    desc: 'Oversees service advisors', tone: 'accent' },
  BDC_AGENT:         { label: 'BDC Agent',         short: 'BDC',        desc: 'The lead queue and conversations', tone: 'info' },
  // Deprecated: split into the two roles below. Kept so existing holders still
  // render while they are migrated.
  SALES_CONSULTANT:  { label: 'Sales Consultant',  short: 'Consultant', desc: 'Legacy – being split into New Car / Used Car', tone: 'info' },
  NEW_CAR_SALES_CONSULTANT:  { label: 'New Car Consultant',  short: 'New Car',  desc: 'Their own assigned leads - new cars', tone: 'info' },
  USED_CAR_SALES_CONSULTANT: { label: 'Used Car Consultant', short: 'Used Car', desc: 'Their own assigned leads - used cars', tone: 'info' },
  // Client renamed 2026-09-30: "Fleet Manager" → "Fleet Consultant" (they sit
  // with the SCs, not the management row of the org chart). Enum id stays
  // FLEET_MANAGER for compatibility; access unchanged (full Deal Desk, own
  // book on leads/appointments/test drives).
  FLEET_MANAGER:     { label: 'Fleet Consultant',  short: 'Fleet',      desc: 'Fleet deals; own book, full Deal Desk', tone: 'info' },
  // Client label 2026-09-30 is "Inventory Controller" (support pod role).
  // Enum id stays INVENTORY_MANAGER; only the display label changes.
  INVENTORY_MANAGER: { label: 'Inventory Controller', short: 'Inventory',  desc: 'Owns stock – stock age, floorplan; no Deal Desk or Customer 360', tone: 'accent' },
  // Test Drive Controller – added 2026-09-30 per the client's revised chart.
  // Support-pod role that orchestrates demos and licence checks across the
  // floor. Dealership-wide on test drives (not scoped to own bookings) via
  // CallerScope; no Deal Desk, no inventory writes.
  TEST_DRIVE_CONTROLLER: { label: 'Test Drive Controller', short: 'TD Ctrl', desc: 'Orchestrates demos and licence checks; owns the Test Drives board', tone: 'info' },
  SERVICE_ADVISOR:   { label: 'Service Advisor',   short: 'Advisor',    desc: 'Workshop bookings + on-the-day journey', tone: 'info' },
  READ_ONLY_AUDITOR: { label: 'Auditor',           short: 'Auditor',    desc: 'Read-only Â· compliance + audit log', tone: 'warning' },
};

/** Roles a dealership admin can grant, in a sensible display order. */
/**
 * Roles a dealership admin can grant, in org-chart order.
 *
 * Exactly the nine the client specified. Four roles that exist in the system are
 * deliberately absent, and each for its own reason:
 *
 *  - SUPER_ADMIN – PMDG operations, cross-tenant. Never grantable from a
 *    dealership's own UI, which is why it was never on the client's list.
 *  - SALES_CONSULTANT – being retired. It still works for those who hold it so
 *    nobody is locked out mid-migration, but no new user should be given it.
 *  - SERVICE_MANAGER / SERVICE_ADVISOR / READ_ONLY_AUDITOR – the client's
 *    structure is sales-only. They remain defined so any existing holder still
 *    renders, but they cannot be assigned.
 *
 * Removing a role from this list stops it being granted; it does not revoke it
 * from anyone who already has it. That is deliberate.
 */
export const ASSIGNABLE_ROLES = [
  // Top level – Dealer Principal + the two GMs at the leadership rank
  // (per the client's revised chart of 2026-09-30 – GM has TWO roles,
  // Sales and Service, both accountable to the Dealer Principal). CEO
  // stays here from the earlier spec.
  'DEALER_PRINCIPAL',
  'GM_SALES',   // "GM – Sales" in the chart
  'GM_SERVICE',        // "GM – Service" – peer of GM – Sales
  'CEO',
  // Sales-side managers (sibling: Sales Manager runs new+fleet, Used Car
  // Manager runs used – per the client's revised chart of 2026-09-30)
  'SALES_MANAGER',
  'USED_CAR_MANAGER',
  // Support pod (report to GM – Sales)
  'INVENTORY_MANAGER',      // "Inventory Controller" in the client's chart
  'TEST_DRIVE_CONTROLLER',  // Owns the Test Drives board
  'BDC_AGENT',
  // Floor consultants
  'FLEET_MANAGER',          // "Fleet Consultant" in the client's chart
  'NEW_CAR_SALES_CONSULTANT',
  'USED_CAR_SALES_CONSULTANT',
];

/**
 * Every role that can appear on a user, assignable or not.
 *
 * Distinct from ASSIGNABLE_ROLES on purpose. Filters and legends need to show
 * roles nobody can be granted any more – during the consultant migration the
 * role you most need to filter by is precisely the retiring one, because that
 * is how you find the people still to move.
 */
export const ALL_ROLES = Object.keys(ROLE_META);

/**
 * Anyone who counts as "a consultant" while the split is underway – the two
 * new roles plus the retiring one. Mirrors CONSULTANT_ROLE_NAMES on the
 * backend. Delete the legacy entry (both sides) once the last holder is
 * migrated. Born of the Team tile proudly reporting "0 bookable consultants"
 * the day everyone moved to the new roles.
 */
export const CONSULTANT_ROLES =
  ['NEW_CAR_SALES_CONSULTANT', 'USED_CAR_SALES_CONSULTANT', 'SALES_CONSULTANT'];

export const roleLabel = (r) => ROLE_META[r]?.label || r;
export const roleShort = (r) => ROLE_META[r]?.short || r;
export const roleTone  = (r) => ROLE_META[r]?.tone || 'accent';

/**
 * Role groups – the mirror of `SecurityRoles.java` in spectraiq-backend.
 *
 * The backend is the authority: it decides what a request may do, and these
 * names and members are copied from it rather than invented here. Keeping the
 * two in step is the whole point – a portal that hides what the server allows
 * is merely annoying, but one that *shows* what the server refuses hands the
 * user a button that 403s, which is worse than not offering it.
 *
 * Regenerate the source of truth with `scripts/extract-role-matrix.mjs` in the
 * backend repo; `docs/role-matrix.md` there lists every endpoint.
 */
export const ROLE_GROUPS = {
  SUPER:       ['SUPER_ADMIN'],
  ADMIN:       ['SUPER_ADMIN', 'DEALER_PRINCIPAL', 'GM_SALES', 'CEO'],
  MANAGEMENT:  ['SUPER_ADMIN', 'DEALER_PRINCIPAL', 'GM_SALES', 'CEO', 'SALES_MANAGER', 'USED_CAR_MANAGER'],
  SALES_STAFF: ['SUPER_ADMIN', 'DEALER_PRINCIPAL', 'GM_SALES', 'CEO', 'SALES_MANAGER', 'USED_CAR_MANAGER', 'BDC_AGENT'],
  ALL_SALES:   ['SUPER_ADMIN', 'DEALER_PRINCIPAL', 'GM_SALES', 'CEO', 'SALES_MANAGER', 'USED_CAR_MANAGER', 'BDC_AGENT',
                'SALES_CONSULTANT', 'NEW_CAR_SALES_CONSULTANT', 'USED_CAR_SALES_CONSULTANT', 'FLEET_MANAGER', 'TEST_DRIVE_CONTROLLER'],
  /**
   * The sales PIPELINE — everyone who works leads and deals. This is
   * ALL_SALES minus the two support-pod roles (Test Drive Controller,
   * Inventory Controller), whose jobs are the demo fleet and the stock
   * respectively, not the pipeline. The nav uses this to keep their
   * sidebar focused: a TDC seeing every lead's score and SLA breach is
   * noise at best and a data-exposure question at worst.
   */
  SALES_PIPELINE: ['SUPER_ADMIN', 'DEALER_PRINCIPAL', 'GM_SALES', 'CEO', 'SALES_MANAGER', 'USED_CAR_MANAGER', 'BDC_AGENT',
                   'SALES_CONSULTANT', 'NEW_CAR_SALES_CONSULTANT', 'USED_CAR_SALES_CONSULTANT', 'FLEET_MANAGER'],
  /** The pipeline plus the Test Drive Controller — for the two screens the
   *  demo-fleet job genuinely needs (Appointments, Test Drives). */
  SALES_PIPELINE_WITH_TDC: ['SUPER_ADMIN', 'DEALER_PRINCIPAL', 'GM_SALES', 'CEO', 'SALES_MANAGER', 'USED_CAR_MANAGER', 'BDC_AGENT',
                            'SALES_CONSULTANT', 'NEW_CAR_SALES_CONSULTANT', 'USED_CAR_SALES_CONSULTANT', 'FLEET_MANAGER', 'TEST_DRIVE_CONTROLLER'],
  /**
   * Deal Desk – mirrors SecurityRoles.DEAL_DESK. Consultants lose quotes (per
   * the client's original brief). BDC keeps the page but is limited to their
   * own quotes server-side. Fleet Consultant (formerly Fleet Manager) has full
   * Deal Desk – confirmed 2026-09-30. Used Car Manager has full Deal Desk on
   * used-car deals (row-scope keeps them to USED-condition rows).
   */
  DEAL_DESK: ['SUPER_ADMIN', 'DEALER_PRINCIPAL', 'GM_SALES', 'CEO', 'SALES_MANAGER', 'USED_CAR_MANAGER',
              'BDC_AGENT', 'FLEET_MANAGER'],
  /** Stock writes – mirrors SecurityRoles.INVENTORY_WRITE. Used Car Manager
   *  writes used stock (approves trade-ins, sets used pricing). */
  INVENTORY_WRITE: ['SUPER_ADMIN', 'DEALER_PRINCIPAL', 'GM_SALES', 'CEO', 'SALES_MANAGER', 'USED_CAR_MANAGER', 'INVENTORY_MANAGER'],
  /** SecurityConfig routes /v1/compliance/** and /v1/audit/** here. */
  COMPLIANCE:  ['SUPER_ADMIN', 'DEALER_PRINCIPAL', 'GM_SALES', 'CEO', 'READ_ONLY_AUDITOR'],
  /**
   * The one endpoint where sales and service meet (platform health recompute).
   * Both service-side leaders included – Service Manager (day-to-day workshop)
   * and GM – Service (leadership peer of GM – Sales per the client's revised
   * chart of 2026-09-30).
   */
  MANAGEMENT_WITH_SERVICE:
    ['SUPER_ADMIN', 'DEALER_PRINCIPAL', 'GM_SALES', 'CEO', 'SALES_MANAGER', 'USED_CAR_MANAGER', 'SERVICE_MANAGER', 'GM_SERVICE'],
  /** Test-drive administration – SecurityRoles.TEST_DRIVE_ADMIN. Owner of the
   *  Test Drives module plus every sales-side manager. Trade-plate register,
   *  dealership-wide test-drive orchestration. */
  TEST_DRIVE_ADMIN:
    ['SUPER_ADMIN', 'DEALER_PRINCIPAL', 'GM_SALES', 'CEO', 'SALES_MANAGER', 'USED_CAR_MANAGER', 'TEST_DRIVE_CONTROLLER'],
};

/**
 * What a signed-in user may DO, as opposed to which pages they may open
 * (that lives on the nav items in lib/apps.js).
 *
 * Every entry below is backed by a real `@PreAuthorize` on the endpoint it
 * describes. Nothing here is aspirational: if the backend does not enforce it,
 * it does not belong in this table, because a check the server does not make
 * is a check a determined user simply skips.
 */
const CAPABILITIES = {
  // POST/PUT/DELETE /v1/users/**
  'users.manage':     ROLE_GROUPS.ADMIN,
  // DELETE /v1/users/{id} – UserController tightens the URL rule to super-admin
  'users.delete':     ROLE_GROUPS.SUPER,
  // PUT /v1/organization-settings, PUT /v1/channels/twilio, voice provider writes
  'settings.edit':    ROLE_GROUPS.ADMIN,
  // GET /v1/organization-settings, GET /v1/channels/**
  'settings.view':    ROLE_GROUPS.MANAGEMENT,
  // POST/PUT/DELETE /v1/assignment-rules, /v1/lost-reasons, /v1/pipeline/sla-config
  'pipeline.edit':    ROLE_GROUPS.MANAGEMENT,
  // POST/PUT/PATCH /v1/vehicles – gated to match the bulk-import endpoint
  'inventory.edit':   ROLE_GROUPS.INVENTORY_WRITE,
  // writes on /v1/voice-agents – mirrors /v1/voice-providers
  'voiceAgents.edit': ROLE_GROUPS.ADMIN,
  // POST /v1/platform/subscription/**, /v1/platform/onboarding/**
  'billing.manage':   ROLE_GROUPS.ADMIN,
  // /v1/compliance/**, /v1/audit/**
  'compliance.view':  ROLE_GROUPS.COMPLIANCE,
  // campaign/survey/dead-lead writes – BDC is view-only, per the client table
  'marketing.edit':   ROLE_GROUPS.MANAGEMENT,
};

/** Does this set of roles grant `capability`? Unknown capability => denied. */
export const can = (roles = [], capability) =>
  (CAPABILITIES[capability] || []).some((r) => (roles || []).includes(r));

/**
 * Generic membership test used by the nav and the route guard.
 *
 * `allowed` being absent means "no restriction recorded" – the page stays open.
 * That is deliberate: the backend leaves many endpoints open to any signed-in
 * user, and the portal must not invent a restriction the server does not make.
 */
export const allowedByRoles = (roles = [], allowed) =>
  !allowed || allowed.length === 0 || allowed.some((r) => (roles || []).includes(r));

/** Can this set of roles manage users? (invite / edit / activate) */
export const canManageUsers = (roles = []) => can(roles, 'users.manage');

/** Only super-admins may hard-delete a user row. */
export const canDeleteUsers = (roles = []) => can(roles, 'users.delete');

export const STATUS_META = {
  ACTIVE:         { label: 'Active',   tone: 'success' },
  INACTIVE:       { label: 'Inactive', tone: 'danger'  },
  PENDING_INVITE: { label: 'Invited',  tone: 'warning' },
};
